import React from "react";
import Nav from "../components/sections/Nav";
import Footer from "../components/sections/Footer";
import RevealOnScroll from "../components/RevealOnScroll";
import Seo from "../components/Seo";
import { BOTANICAL_SRC } from "../components/BotanicalOverlay";

/* ---------------------------------------------------------------------------
 * Palette (iter_15 update — replaces gold + terracotta / warm salmon accents
 * with dusty rose-purple + deep berry. All "Book a Reading" buttons across
 * both /alchemy-extended and /reflection-deck share BOOK_BTN_COLOR.
 * ------------------------------------------------------------------------- */
const LINEN = "#FAFAF8";        // near-white off-white
const IVORY = "#FFFFFF";        // stationery card surface (pure white now)
const BERRY = "#7A2D50";        // deep berry — italic accents, borders, hover flood, marquee
const TEAL = "#5C7A78";         // dusty muted teal — card section background
const NAVY = "#2B3A52";         // deep navy — secondary dark / closing CTA (unchanged)
const GOLD = "#A88CA0";         // legacy name — remapped to dusty rose-purple (no gold anywhere)
const ROSE_PURPLE = "#A88CA0";  // soft lavender-leaning mauve — corner ornaments, hairlines, small accents
const CHARCOAL = "#2C2825";
const TAUPE = "#7C6E7A";        // cool mauve-taupe

const BOOK_BTN_COLOR = "#7A2D50"; // unified button color for every "Book a Reading" on this page

const HERO_IMG =
    "https://customer-assets.emergentagent.com/job_practice-portal-15/artifacts/f1xkwf1v_55661745391592969.jpg";

/* ---------------------------------------------------------------------------
 * Small primitives
 * ------------------------------------------------------------------------- */
const Eyebrow = ({ children, color = TAUPE }) => (
    <span
        className="text-[12px] sm:text-[13px] uppercase font-medium"
        style={{
            color,
            letterSpacing: "0.34em",
            fontFamily: "'DM Sans', sans-serif",
        }}
    >
        {children}
    </span>
);

const VictorianDivider = ({ color = GOLD, width = 320 }) => (
    <div
        aria-hidden
        className="mx-auto flex items-center justify-center"
        style={{ width, maxWidth: "80%" }}
    >
        <span style={{ flex: 1, height: 1, background: color, opacity: 0.7 }} />
        <svg width="44" height="14" viewBox="0 0 44 14" fill="none" style={{ margin: "0 14px", color }} aria-hidden>
            <path
                d="M2 7 C 7 7, 9 2, 14 7 C 19 12, 19 2, 24 7 C 29 12, 31 7, 36 7 L 42 7"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
                fill="none"
                opacity="0.9"
            />
            <circle cx="22" cy="7" r="2.2" stroke="currentColor" strokeWidth="0.8" fill="none" />
            <circle cx="22" cy="7" r="0.8" fill="currentColor" />
        </svg>
        <span style={{ flex: 1, height: 1, background: color, opacity: 0.7 }} />
    </div>
);

const StationeryFrame = ({ color = GOLD, inset = 0 }) => (
    <div
        aria-hidden
        style={{
            position: "absolute",
            inset,
            pointerEvents: "none",
            border: `1px solid ${color}`,
            opacity: 0.55,
        }}
    >
        <div
            style={{
                position: "absolute",
                inset: 6,
                border: `1px solid ${color}`,
                opacity: 0.7,
            }}
        />
        {[
            { top: -10, left: -10 },
            { top: -10, right: -10 },
            { bottom: -10, left: -10 },
            { bottom: -10, right: -10 },
        ].map((pos, i) => (
            <span
                key={i}
                style={{
                    position: "absolute",
                    ...pos,
                    width: 20,
                    height: 20,
                    color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 16,
                    lineHeight: 1,
                    background: "transparent",
                }}
                aria-hidden
            >
                ✦
            </span>
        ))}
    </div>
);

/* ---------------------------------------------------------------------------
 * HERO — mirrors homepage layout, brick-arch photograph
 * ------------------------------------------------------------------------- */
const Hero = () => (
    <section
        id="extended-top"
        data-testid="alchemy-extended-hero"
        className="relative min-h-[100vh] flex items-end overflow-hidden"
        style={{ background: LINEN }}
    >
        <div className="absolute inset-y-0 right-0 w-full lg:w-[60%] overflow-hidden">
            <div className="absolute inset-0 animate-ken-burns">
                <img
                    src={HERO_IMG}
                    alt="An arched garden gate with climbing pink roses, the carved wooden door slightly ajar, opening into a sunlit garden beyond"
                    className="h-full w-full object-cover"
                    style={{ filter: "brightness(1.06) saturate(0.96)" }}
                />
            </div>
        </div>
        {/* Static overlays — matched to homepage hero so text sits cleanly on the photo */}
        <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
                background:
                    "linear-gradient(90deg, #FAFAF8 0%, rgba(250,250,248,0.78) 25%, rgba(238,242,237,0.50) 60%, rgba(238,242,237,0.55) 100%)",
            }}
        />
        <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
                background:
                    "linear-gradient(180deg, transparent 0%, rgba(238,242,237,0.3) 100%)",
            }}
        />

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-12 pt-32 pb-20 lg:pb-28">
            <div className="max-w-3xl">
                <Eyebrow color={BERRY}>Beyond the Clinical Hour</Eyebrow>

                <h1
                    data-testid="alchemy-extended-headline"
                    className="mt-6 font-serif text-balance"
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontWeight: 500,
                        color: CHARCOAL,
                        fontSize: "clamp(44px, 6.4vw, 80px)",
                        lineHeight: 1.02,
                        letterSpacing: "-0.008em",
                    }}
                >
                    The Extended{" "}
                    <em
                        style={{
                            fontStyle: "italic",
                            color: BERRY,
                            fontWeight: 500,
                        }}
                    >
                        Path.
                    </em>
                </h1>

                <p
                    className="mt-8 max-w-xl"
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: "clamp(23px, 2.2vw, 30px)",
                        color: CHARCOAL,
                        lineHeight: 1.35,
                        fontWeight: 400,
                    }}
                >
                    There are questions that numbers and diagnoses{" "}
                    <em
                        style={{
                            fontStyle: "italic",
                            color: BERRY,
                            fontWeight: 500,
                        }}
                    >
                        cannot answer.
                    </em>
                </p>

                <p
                    className="mt-8 max-w-xl"
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: "clamp(20px, 1.85vw, 25px)",
                        color: CHARCOAL,
                        lineHeight: 1.55,
                        fontStyle: "italic",
                        opacity: 0.92,
                    }}
                >
                    Some of the most meaningful work happens at the intersection of the
                    psychological and the sacred. This is that space.
                </p>

                <div
                    className="mt-14 flex items-center gap-3"
                    style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 13,
                        letterSpacing: "0.32em",
                        textTransform: "uppercase",
                        color: TAUPE,
                    }}
                >
                    <span style={{ color: GOLD, fontSize: 18, lineHeight: 1 }}>✦</span>
                    Step through, gently
                </div>
            </div>
        </div>
    </section>
);

/* ---------------------------------------------------------------------------
 * WHAT THIS SPACE IS
 * ------------------------------------------------------------------------- */
const WhatThisSpaceIs = () => (
    <section
        data-testid="alchemy-extended-what-this-space-is"
        className="relative overflow-hidden"
        style={{ background: LINEN, paddingTop: 160, paddingBottom: 160 }}
    >
        <div className="relative mx-auto max-w-[860px] px-6 lg:px-8">
            <div className="text-center" data-reveal>
                <Eyebrow color={TAUPE}>What This Space Is</Eyebrow>
                <h2
                    className="mt-6 text-balance"
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: "clamp(34px, 4.4vw, 56px)",
                        lineHeight: 1.08,
                        color: CHARCOAL,
                        fontWeight: 400,
                    }}
                >
                    A different{" "}
                    <em style={{ fontStyle: "italic", color: BERRY }}>kind of knowing</em>
                </h2>
                <div className="mt-9">
                    <VictorianDivider color={GOLD} width={260} />
                </div>
            </div>

            <div
                className="mt-14 mx-auto"
                style={{ maxWidth: 760 }}
                data-reveal
            >
                <p
                    style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: "19px",
                        lineHeight: 1.9,
                        color: CHARCOAL,
                        opacity: 0.94,
                    }}
                >
                    For those who feel drawn to this work, we lean into the astrological
                    energies that a person is experiencing or moving toward — creating a more
                    thoughtful, intentional way to meet challenges before they arrive. Rather
                    than being caught off guard by difficulty, we use these energies as a map
                    for navigating what is already in motion.
                </p>
                <p
                    className="mt-7"
                    style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: "19px",
                        lineHeight: 1.9,
                        color: CHARCOAL,
                        opacity: 0.94,
                    }}
                >
                    Tarot works similarly but speaks more directly. Each card carries a
                    message — something to sit with, reflect on, and return to. It is less
                    about prediction and more about illumination. A mirror held at a different
                    angle.
                </p>
                <p
                    className="mt-7"
                    style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: "19px",
                        lineHeight: 1.9,
                        color: CHARCOAL,
                        opacity: 0.94,
                    }}
                >
                    I came to astrology and tarot because they answered questions that
                    clinical frameworks sometimes could not. They helped me make meaning of
                    difficult situations in a way that felt true rather than just logical. I
                    bring that same spirit into this work — not as a replacement for therapy,
                    but as a companion to it. An invitation to know yourself from a different
                    direction.
                </p>

                <blockquote
                    data-testid="alchemy-extended-blockquote"
                    className="mt-14 mx-auto"
                    style={{
                        borderLeft: `3px solid ${BERRY}`,
                        paddingLeft: 28,
                        paddingTop: 8,
                        paddingBottom: 8,
                        maxWidth: 680,
                        fontFamily: "'Cormorant Garamond', serif",
                        fontStyle: "italic",
                        fontSize: "30px",
                        lineHeight: 1.45,
                        color: CHARCOAL,
                    }}
                >
                    “The most meaningful work happens when we stop asking what is wrong and
                    start asking what is trying to emerge.”
                </blockquote>
            </div>
        </div>
    </section>
);

/* ---------------------------------------------------------------------------
 * OFFERINGS — 3 cards, hover-flood deep berry, on dusty teal background
 * ------------------------------------------------------------------------- */
const OFFERINGS = [
    {
        testid: "offering-tarot",
        label: "The Reflection Deck",
        subLabel: "Tarot",
        accent: BERRY,
        headline: "The cards as a mirror.",
        headlineEm: "The cards as a map.",
        body:
            "Tarot at Alchemy Practice is offered two ways — as a standalone depth-reading path open to anyone, and as a therapeutic tool woven into licensed psychotherapy for clients who want to take the work deeper. Both paths use the cards the same way: not to predict, but to illuminate.",
        linkText: "Explore the Reflection Deck",
        linkHref: "/alchemy-extended/alchemy-readings",
        badge: null,
    },
    {
        testid: "offering-inner-compass",
        label: "The Inner Compass",
        subLabel: "Astrology",
        accent: NAVY,
        headline: "Your chart is not your destiny.",
        headlineEm: "It is your invitation.",
        body:
            "For those who want to understand the energies at play in their lives — and learn how to move with them rather than against them. A space for reflection, meaning-making, and the kind of knowing that goes beyond what the analytical mind can reach.",
        linkText: "Join the Waitlist",
        linkHref: "/contact",
        badge: "Coming Fall 2026",
    },
    {
        testid: "offering-alchemists-table",
        label: "The Alchemist's Table",
        subLabel: "Resources & Tools",
        accent: TEAL,
        headline: "A place to",
        headlineEm: "return to.",
        body:
            "A curated collection of tools, reflections, and resources gathered for the people doing this work. Come back whenever you need something to hold onto, return to, or carry forward.",
        linkText: "Coming Late Summer",
        linkHref: "/contact",
        badge: null,
    },
];

const FloodCard = ({ offering }) => {
    const [hover, setHover] = React.useState(false);
    const accent = offering.accent || BERRY;
    const onSurface = hover ? "#FFFFFF" : CHARCOAL;
    const onSurfaceSoft = hover ? "rgba(255,255,255,0.85)" : "rgba(44,40,37,0.85)";

    return (
        <article
            data-testid={offering.testid}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            className="relative flex flex-col h-full overflow-hidden transition-colors duration-500"
            style={{
                background: hover ? accent : "#FFFFFF",
                border: `1px solid ${hover ? accent : "rgba(44,40,37,0.10)"}`,
                padding: "56px 40px 48px",
                color: onSurface,
            }}
        >
            {/* top accent line — distinguishes each card */}
            <div
                aria-hidden
                className="transition-colors duration-500"
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: hover ? "rgba(255,255,255,0.85)" : accent,
                }}
            />
            {/* corner star */}
            <span
                aria-hidden
                className="transition-colors duration-500"
                style={{
                    position: "absolute",
                    top: 22,
                    right: 22,
                    color: hover ? "rgba(255,255,255,0.9)" : accent,
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 22,
                    lineHeight: 1,
                }}
            >
                ✦
            </span>

            <div className="relative z-10 flex flex-col h-full">
                <span
                    className="transition-colors duration-500"
                    style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 13,
                        letterSpacing: "0.34em",
                        textTransform: "uppercase",
                        color: hover ? "rgba(255,255,255,0.95)" : accent,
                        fontWeight: 500,
                    }}
                >
                    {offering.label}
                </span>
                <span
                    className="mt-2 transition-colors duration-500"
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontStyle: "italic",
                        fontSize: "18px",
                        color: onSurfaceSoft,
                    }}
                >
                    {offering.subLabel}
                </span>

                {offering.badge && (
                    <span
                        data-testid={`${offering.testid}-badge`}
                        className="mt-4 inline-block transition-colors duration-500"
                        style={{
                            alignSelf: "flex-start",
                            padding: "6px 14px",
                            border: `1px solid ${hover ? "rgba(255,255,255,0.85)" : accent}`,
                            color: hover ? "#FFFFFF" : accent,
                            background: hover ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.02)",
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 12,
                            letterSpacing: "0.22em",
                            textTransform: "uppercase",
                            borderRadius: 999,
                        }}
                    >
                        {offering.badge}
                    </span>
                )}

                <h3
                    className="mt-6 transition-colors duration-500"
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: "36px",
                        lineHeight: 1.15,
                        color: onSurface,
                        fontWeight: 500,
                    }}
                >
                    {offering.headline}{" "}
                    <em
                        className="transition-colors duration-500"
                        style={{
                            fontStyle: "italic",
                            color: hover ? "rgba(255,255,255,0.92)" : accent,
                            fontWeight: 500,
                        }}
                    >
                        {offering.headlineEm}
                    </em>
                </h3>
                <div
                    aria-hidden
                    className="transition-colors duration-500"
                    style={{
                        marginTop: 22,
                        marginBottom: 22,
                        height: 1,
                        width: 56,
                        background: hover ? "rgba(255,255,255,0.7)" : accent,
                        opacity: 0.85,
                    }}
                />
                <p
                    className="transition-colors duration-500"
                    style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: "18px",
                        lineHeight: 1.85,
                        color: onSurfaceSoft,
                    }}
                >
                    {offering.body}
                </p>

                <div className="mt-auto pt-10">
                    <a
                        href={offering.linkHref}
                        data-testid={`${offering.testid}-link`}
                        className="inline-flex items-center gap-3 transition-colors duration-500"
                        style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: "13px",
                            letterSpacing: "0.3em",
                            textTransform: "uppercase",
                            color: hover ? "#FFFFFF" : accent,
                            borderBottom: `1px solid ${hover ? "#FFFFFF" : accent}`,
                            paddingBottom: 6,
                            fontWeight: 500,
                        }}
                    >
                        {offering.linkText}
                        <span aria-hidden style={{ transform: "translateY(-1px)" }}>→</span>
                    </a>
                </div>
            </div>
        </article>
    );
};

const Offerings = () => (
    <section
        data-testid="alchemy-extended-offerings"
        className="relative overflow-hidden"
        style={{ background: "#E0EBE6", paddingTop: 128, paddingBottom: 144 }}
    >
        {/* subtle botanical overlay corners */}
        <div
            aria-hidden
            style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                zIndex: 0,
                backgroundImage: `url(${BOTANICAL_SRC})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right -60px bottom -80px",
                backgroundSize: "640px auto",
                opacity: 0.06,
                mixBlendMode: "multiply",
            }}
        />
        <div
            aria-hidden
            style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                zIndex: 0,
                backgroundImage: `url(${BOTANICAL_SRC})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "left -80px top -60px",
                backgroundSize: "520px auto",
                opacity: 0.05,
                mixBlendMode: "multiply",
            }}
        />

        <div className="relative mx-auto max-w-[1240px] px-6 lg:px-10" style={{ zIndex: 1 }}>
            <div className="text-center" data-reveal>
                <Eyebrow color={TEAL}>Where would you like to begin</Eyebrow>
                <h2
                    className="mt-6 text-balance"
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: "clamp(36px, 4.5vw, 58px)",
                        lineHeight: 1.1,
                        color: CHARCOAL,
                        fontWeight: 400,
                    }}
                >
                    Two doorways into{" "}
                    <em style={{ fontStyle: "italic", color: BERRY }}>deeper work.</em>
                </h2>
                <p
                    className="mt-6 mx-auto"
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontStyle: "italic",
                        fontSize: "22px",
                        color: TAUPE,
                        lineHeight: 1.6,
                        maxWidth: 680,
                    }}
                >
                    Each one stands quietly open — no need to choose just one.
                </p>
                <div className="mt-10">
                    <VictorianDivider color={GOLD} width={240} />
                </div>
            </div>

            <div
                className="mt-16 grid md:grid-cols-3 gap-7 lg:gap-9"
                data-reveal
            >
                {OFFERINGS.map((o) => (
                    <FloodCard key={o.testid} offering={o} />
                ))}
            </div>
        </div>
    </section>
);

/* ---------------------------------------------------------------------------
 * MARQUEE — deep berry background, white text, two phrases scrolling
 * ------------------------------------------------------------------------- */
const MARQUEE_PHRASES = [
    "The chart does not predict your life. It illuminates it.",
    "Every card is a mirror. Every mirror is an invitation.",
];

const Marquee = () => {
    // Repeat phrases enough times to fill a wide screen and loop seamlessly
    const segment = (
        <>
            {MARQUEE_PHRASES.map((p, i) => (
                <span key={i} className="flex items-center gap-8 mx-8">
                    <span style={{ color: GOLD, fontSize: 16 }}>✦</span>
                    <span
                        style={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontStyle: "italic",
                            fontSize: 26,
                            color: "#FFFFFF",
                            whiteSpace: "nowrap",
                            letterSpacing: "0.01em",
                        }}
                    >
                        {p}
                    </span>
                </span>
            ))}
        </>
    );

    return (
        <section
            data-testid="alchemy-extended-marquee"
            className="relative overflow-hidden"
            style={{ background: BERRY }}
            aria-label="Alchemy Extended marquee"
        >
            <div className="flex" style={{ paddingTop: 26, paddingBottom: 26 }}>
                <div className="flex shrink-0 animate-marquee items-center">
                    {segment}
                    {segment}
                    {segment}
                </div>
                <div className="flex shrink-0 animate-marquee items-center" aria-hidden>
                    {segment}
                    {segment}
                    {segment}
                </div>
            </div>
        </section>
    );
};

/* ---------------------------------------------------------------------------
 * CLOSING CTA — kept (navy "Not sure where to begin?")
 * ------------------------------------------------------------------------- */
const Closing = () => (
    <section
        data-testid="alchemy-extended-closing"
        className="relative overflow-hidden"
        style={{ background: NAVY, paddingTop: 128, paddingBottom: 128 }}
    >
        <div
            aria-hidden
            style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                zIndex: 0,
                backgroundImage: `url(${BOTANICAL_SRC})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right -100px bottom -120px",
                backgroundSize: "680px auto",
                opacity: 0.07,
                mixBlendMode: "screen",
                filter: "invert(1) brightness(1.15)",
            }}
        />
        <div
            aria-hidden
            style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                zIndex: 0,
                backgroundImage: `url(${BOTANICAL_SRC})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "left -120px top -100px",
                backgroundSize: "520px auto",
                opacity: 0.06,
                mixBlendMode: "screen",
                filter: "invert(1) brightness(1.15)",
            }}
        />
        <div className="relative mx-auto max-w-[900px] px-6 lg:px-10" style={{ zIndex: 1 }}>
            <div className="relative" style={{ padding: "64px 28px" }}>
                <StationeryFrame color={GOLD} inset={0} />
                <div className="text-center" data-reveal>
                    <Eyebrow color={GOLD}>A quiet conversation</Eyebrow>
                    <h2
                        className="mt-6 text-balance"
                        style={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontSize: "clamp(38px, 4.5vw, 56px)",
                            lineHeight: 1.1,
                            color: IVORY,
                            fontWeight: 400,
                        }}
                    >
                        Not sure where to <em style={{ fontStyle: "italic", color: GOLD }}>begin?</em>
                    </h2>
                    <div className="mt-8">
                        <VictorianDivider color={GOLD} width={220} />
                    </div>
                    <p
                        className="mt-9 mx-auto"
                        style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: "18px",
                            lineHeight: 1.85,
                            color: "#EEF2F8",
                            opacity: 0.95,
                            maxWidth: 680,
                        }}
                    >
                        Both offerings are open to anyone regardless of location. If you are
                        currently a therapy client and are curious about the extended work, reach
                        out directly. If you are new here and want to find the right starting
                        point, a quiet conversation is always the best first step.
                    </p>

                    <div className="mt-12">
                        <a
                            href="/contact"
                            data-testid="alchemy-extended-cta-button"
                            className="inline-flex items-center justify-center transition-all duration-300"
                            style={{
                                background: GOLD,
                                color: NAVY,
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: "13px",
                                letterSpacing: "0.32em",
                                textTransform: "uppercase",
                                fontWeight: 500,
                                padding: "18px 40px",
                                border: `1px solid ${GOLD}`,
                                borderRadius: 0,
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = "transparent";
                                e.currentTarget.style.color = GOLD;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = GOLD;
                                e.currentTarget.style.color = NAVY;
                            }}
                        >
                            Get In Touch
                        </a>
                    </div>

                    <p
                        className="mt-12"
                        style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: "13px",
                            letterSpacing: "0.18em",
                            color: "#D5DCE6",
                            opacity: 0.92,
                        }}
                    >
                        alchemypractice.com&nbsp;&middot;&nbsp;office@alchemypractice.com&nbsp;&middot;&nbsp;(608) 205-8515
                    </p>
                </div>
            </div>
        </div>
    </section>
);

/* ---------------------------------------------------------------------------
 * IN THE MEANTIME
 * ------------------------------------------------------------------------- */
const InTheMeantime = () => (
    <section
        data-testid="alchemy-extended-in-the-meantime"
        className="relative overflow-hidden"
        style={{ background: "#EEE5EA", paddingTop: 140, paddingBottom: 152 }}
    >
        <div className="relative mx-auto max-w-[820px] px-6 lg:px-8 text-center" data-reveal>
            <Eyebrow color={BERRY}>Virtual & Email Readings Available</Eyebrow>
            <p
                className="mt-8 mx-auto"
                style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: "clamp(28px, 2.7vw, 36px)",
                    lineHeight: 1.4,
                    color: CHARCOAL,
                    fontStyle: "italic",
                    maxWidth: 720,
                    fontWeight: 500,
                }}
            >
                Both of these spaces are being tended to carefully. If you feel called to this
                work before they open, reach out. Some conversations do not need to wait.
            </p>
            <div className="mt-12">
                <a
                    href="/contact"
                    data-testid="alchemy-extended-send-a-note"
                    className="inline-flex items-center justify-center transition-all duration-300"
                    style={{
                        background: BERRY,
                        color: "#FFFFFF",
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 14,
                        letterSpacing: "0.32em",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        padding: "18px 36px",
                        border: `1px solid ${BERRY}`,
                        borderRadius: 0,
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = BERRY;
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = BERRY;
                        e.currentTarget.style.color = "#FFFFFF";
                    }}
                >
                    Send a Note
                </a>
            </div>
        </div>
    </section>
);

/* ---------------------------------------------------------------------------
 * MEET AUSTINE (shortened bio) + BOOK A READING CTA
 * Iter_15: added per user request — mirrors the fuller bio on /reflection-deck
 * but distilled to three paragraphs. Includes a portrait placeholder Austine
 * can swap for her own headshot, plus a single unified "Book a Reading" CTA.
 * ------------------------------------------------------------------------- */
const MeetAustineBrief = () => (
    <section
        data-testid="alchemy-extended-meet-austine"
        className="relative overflow-hidden"
        style={{ background: LINEN, paddingTop: 120, paddingBottom: 40 }}
    >
        <div className="mx-auto max-w-[1200px] px-6 lg:px-12" data-reveal>
            <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-14 lg:gap-20 items-start">
                {/* Portrait placeholder — Austine can swap for her own photo */}
                <div className="lg:sticky lg:top-24 self-start" data-testid="alchemy-extended-meet-portrait">
                    <div
                        className="relative overflow-hidden"
                        style={{ border: `1px solid ${ROSE_PURPLE}`, padding: 10, background: "#FFFFFF" }}
                    >
                        <img
                            src="https://customer-assets.emergentagent.com/job_practice-portal-15/artifacts/fwvbl4l9_Facetune_02-11-2025-15-31-34.jpeg"
                            alt="Austine — licensed mental health therapist and tarot reader"
                            className="w-full h-[460px] object-cover object-top"
                            loading="lazy"
                        />
                        <span
                            className="absolute -top-3 -left-3 px-3 py-1 text-[10px] tracking-[0.3em] uppercase"
                            style={{ background: BERRY, color: LINEN, fontFamily: "'DM Sans', sans-serif" }}
                        >
                            Austine
                        </span>
                    </div>
                </div>

                <div className="space-y-6">
                    <Eyebrow color={BERRY}>Meet Austine</Eyebrow>
                    <h2
                        style={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontSize: "clamp(32px, 3.6vw, 46px)",
                            lineHeight: 1.15,
                            color: TEAL,
                            fontWeight: 500,
                        }}
                    >
                        In relationship with the cards{" "}
                        <em style={{ fontStyle: "italic", color: BERRY }}>since 1996.</em>
                    </h2>
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 17, lineHeight: 1.9, color: "rgba(44,40,37,0.88)" }}>
                        Austine has been in a relationship with the cards since 1996. In 2017,
                        during one of the harder chapters of her life, they became a way back to
                        herself — reflecting the feelings she hadn&rsquo;t yet found words for,
                        and helping her get in touch with an intuition she had been too afraid to
                        trust.
                    </p>
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 17, lineHeight: 1.9, color: "rgba(44,40,37,0.88)" }}>
                        Austine is a licensed mental health therapist specializing in complex
                        trauma. She holds every reading with the same care she brings to clinical
                        work.
                    </p>
                    <p
                        style={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontStyle: "italic",
                            fontSize: 21,
                            lineHeight: 1.55,
                            color: TEAL,
                            borderLeft: `3px solid ${ROSE_PURPLE}`,
                            paddingLeft: 22,
                            marginTop: 8,
                        }}
                    >
                        This is not therapy. It is a quiet, honest encounter with where you are
                        — and what that might be asking of you.
                    </p>
                </div>
            </div>
        </div>
    </section>
);

const ExtendedBookCTA = () => (
    <section
        data-testid="alchemy-extended-book-cta-section"
        className="relative"
        style={{ background: LINEN, paddingTop: 12, paddingBottom: 120, textAlign: "center" }}
    >
        <div className="mx-auto max-w-[720px] px-6" data-reveal>
            <a
                href="/book-a-reading"
                data-testid="alchemy-extended-book-a-reading-cta"
                className="inline-flex items-center justify-center gap-2 transition-opacity duration-300 hover:opacity-90"
                style={{
                    background: BOOK_BTN_COLOR,
                    color: "#FAFAF8",
                    padding: "20px 44px",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 13,
                    letterSpacing: "0.32em",
                    textTransform: "uppercase",
                    fontWeight: 500,
                }}
            >
                Book a Reading <span aria-hidden>→</span>
            </a>
        </div>
    </section>
);

/* ---------------------------------------------------------------------------
 * PAGE
 * ------------------------------------------------------------------------- */
export default function AlchemyExtendedPage() {
    return (
        <main data-testid="alchemy-extended-page" style={{ background: LINEN, color: CHARCOAL }}>
            <Seo
                title="The Extended Path | Alchemy Practice"
                description="Beyond the clinical hour. Astrology, tarot, and curated resources for those doing deep inner work — held with psychological depth alongside Alchemy Practice."
            />
            <Nav />
            <RevealOnScroll>
                <Hero />
                <WhatThisSpaceIs />
                <Offerings />
                <Marquee />
                <MeetAustineBrief />
                <ExtendedBookCTA />
                <Closing />
                <InTheMeantime />
                <Footer />
            </RevealOnScroll>

            {/* Marquee animation styles */}
            <style>{`
                @keyframes alchemy-extended-marquee {
                    from { transform: translateX(0); }
                    to   { transform: translateX(-100%); }
                }
                .animate-marquee {
                    animation: alchemy-extended-marquee 48s linear infinite;
                }
                @media (prefers-reduced-motion: reduce) {
                    .animate-marquee { animation: none; }
                }
            `}</style>
        </main>
    );
}
